import clsx from 'clsx';
import { memo, PropsWithChildren, ReactNode, useCallback, useContext } from 'react';
import { I18n } from 'src/modules/common/components/I18n/I18n';
import { MESSAGES } from 'src/modules/common/constants';
import { PDFIcon } from 'src/modules/common/icons/PDFIcon';
import { ProductImage } from 'src/modules/common/components/ProductImage/ProductImage';
import { ActuatorIcon } from 'src/modules/common/icons/ActuatorIcon.tsx';
import { preventDefault } from 'src/modules/common/utils/preventDefault.ts';
import { RenderHtml } from 'src/modules/common/components/RenderHtml/RenderHtml.tsx';
import { AccessoriesPageData } from 'src/modules/accessories/AccessoriesPageMapping.ts';
import { ConfigContext } from 'src/configContext';
import { ViewProductLink } from 'src/modules/common/components/ViewProductLink/ViewProductLink';
import { createFileGaClickEvent } from 'src/utils/createFileGaClickEvent.ts';

type Props = PropsWithChildren<{
    selected?: boolean;
    accessory: AccessoriesPageData['vssAccessories']['valveAccessories'][0]['item'];
    chip?: ReactNode;
    price?: ReactNode;
    onSelect?: (label: string) => void;
}>

export const Accessory = memo(({ selected, accessory, chip, price, onSelect, children }: Props) => {
    const { userSettings, gaPush } = useContext(ConfigContext);

    const statical = onSelect === undefined;

    const onClick = useCallback(() => {
        if (onSelect) {
            onSelect(accessory.Label);
        }
    }, [onSelect, accessory]);

    const onFileClick = useCallback((url: string) => {
        gaPush(createFileGaClickEvent(url));
    }, [gaPush]);

    return (
        <div
            onKeyDown={() => null}
            role="button"
            tabIndex={0}
            className={clsx({
                'vss-accessory': true,
                'vss-accessory--selected': selected,
                'vss-accessory--statical': statical,
            })}
            onClick={onClick}
        >
            <div className="vss-accessory__header">
                <div className="vss-accessory__details">
                    <div className="vss-accessory__product">
                        <ViewProductLink
                            userSettings={userSettings}
                            code={accessory.Label}
                        >
                            {accessory.Label}
                        </ViewProductLink>

                        {chip && (
                            <div className="vss-accessory__recommendation">
                                {chip}
                            </div>
                        )}
                    </div>
                </div>

                {!statical && (
                    <div
                        data-testid="checkbox"
                        className={clsx({
                            'vss-accessory__checkbox': true,
                            'vss-accessory__checkbox--checked': selected,
                        })}
                    />
                )}
            </div>

            <div className="vss-accessory__body">
                <div className="vss-accessory__content-wrapper">
                    <div className="vss-accessory__image">
                        <ProductImage
                            imgSrc={accessory.image?.raw.join('') || ''}
                        >
                            <ActuatorIcon/>
                        </ProductImage>
                    </div>

                    <div className="vss-accessory__content">
                        <div
                            className="vss-accessory__type"
                            role="row"
                            tabIndex={0}
                            onKeyDown={() => null}
                            onClick={preventDefault}
                        >
                            <RenderHtml data={accessory.accessoryType?.localized.join('') || ''}/>
                        </div>

                        <div
                            className="vss-accessory__description"
                            role="row"
                            tabIndex={0}
                            onKeyDown={() => null}
                            onClick={preventDefault}
                        >
                            <RenderHtml data={accessory.description?.localized.join('') || ''}/>
                        </div>
                    </div>
                </div>

                {price && (
                    <div className="vss-accessory__price">
                        <div className="vss-accessory__price-label">
                            <I18n messageId={MESSAGES.DevicePricePerUnit}/>
                        </div>

                        <div className="vss-accessory__price-value">
                            {price}
                        </div>
                    </div>
                )}

                <div className="vss-accessory__footer">
                    <div className="vss-accessory__links">
                        {accessory.specification && (
                            <a
                                href={accessory.specification.localized.join('')}
                                onClick={() => accessory.specification && onFileClick(
                                    accessory.specification.localized.join(''),
                                )}
                                className="vss-accessory__link"
                                target="_blank"
                                rel="noreferrer"
                            >
                                <I18n messageId={MESSAGES._660_Data_sheet_PDF}/>

                                <span className="vss-accessory__link-icon">
                                    <PDFIcon/>
                                </span>
                            </a>
                        )}
                    </div>

                    <div className="vss-accessory__actions">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
});
